import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, Row, Col, Card, Button,Badge, Alert } from "reactstrap";
import { useDispatch,useSelector } from "react-redux";

import logoSm from "../assets/images/logo-sm.png";
import logoDark from "../assets/images/logo-dark.png";
import logoLight from "../assets/images/logo-light.png";

import ProfileDropdown from '../Components/Common/ProfileDropdown';
import LightDark from '../Components/Common/LightDark';
import LanguageDropdown from '../Components/Common/LanguageDropdown';
import { LanguageContext } from '../Components/Contexts/LanguageContext';
import MainContext from '../Components/Contexts/MainContext'; // MainContext
import FairContext from '../Components/Contexts/Management/FairContext'; // FairContext
import FuarLogoImg from '../Components/Fairs/FuarLogoImg';
import FuarBilgisiModal from '../Components/Fairs/FuarBilgisiModal';
import {changeSidebarVisibility} from '../slices/layouts/thunk';
import { createSelector } from 'reselect';
import { isFirmaAccount } from '../services/firmaSessionService';
import { apiErrorMessage } from '../services/fuarService';
import { usePortal } from '../Components/Contexts/PortalContext';
import FirmaEgitimi from '../Components/Firma/FirmaEgitimi'; // kullanım turu: Katılımcı girişlerinde "?" düğmesi, anahtar kapalıyken hiçbir şey çizmez
import { useTranslation } from 'react-i18next';
import { currentLocaleTag } from '../i18n';

// The red pill naming the panel the login sees ("Yabancı Katılımcı") when the company has a single interface set. Switched off (passive, not
// deleted) at Deniz's request, 2026-10-23: flip to true to bring it back. The interface switcher (several sets) is unaffected.
const SHOW_PANEL_NAME_PILL = false;

const Header = ({ onChangeLayoutMode, layoutModeType, headerClass }) => {
    const dispatch = useDispatch();
    const { selectedFuarId, setSelectedFuarId, fetchActiveFairId, menuItemClicked, setMenuItemClicked } = useContext(MainContext);
    const { getFairs, getSelectableFairs, getActiveFair, activateFair } = useContext(FairContext);
    const { selectedLang, setSelectedLang } = useContext(LanguageContext);
    // #311: the company portal's interface sets at the selected fair (null for staff).
    const portal = usePortal();
    const [activeFairLogo, setActiveFairLogo] = useState(null); // null | { url, fileName }
    const [modalOpen, setModalOpen] = useState(false);
    const [fairs, setFairs] = useState([]);
    const [fairsLoadFailed, setFairsLoadFailed] = useState(false);
    const [activateError, setActivateError] = useState(null);
    const [detayFuar, setDetayFuar] = useState(null); // #561: the fair whose FuarBilgisiModal is open (a /fuar/list item)

    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.id;
    // Backlog #187: a firma account (groups 9/19, or no group) picks only among its company's fairs.
    const firmaAccount = isFirmaAccount();
    // WAS (until #332): const hasFetchedFairs = useRef(false); // fuar listesinin sadece bir kez yüklenmesini sağlar
    // #332: the language the fair list was fetched in -- fetched once per language, since the backend
    // answers fair names in the UI language.
    const hasFetchedFairs = useRef(null);
    const { t } = useTranslation('header');

    const selectDashboardData = createSelector(
        (state) => state.Layout,
        (state) => ({ sidebarVisibilitytype: state.sidebarVisibilitytype })
    );
    const { sidebarVisibilitytype } = useSelector(selectDashboardData);

    // Backlog #44: MainProvider (an ancestor of Header in every layout) already fetches the active
    // fuar once on app mount — see MainContext.js's own `run(); }, [])` effect. Header used to repeat
    // the exact same call here, doubling the request on every page load for no benefit.
    // WAS:
    // useEffect(() => {
    //     if (userId) {
    //         fetchActiveFairId();
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []); // sadece mount'ta çalış

    // Fuar listesini çek - sadece bir kez. Backlog #44: no more hard slice(0, 5) in raw backend
    // order (which could show an archived fair) — modalFairs below picks what the switcher actually
    // shows, from the full list.
    useEffect(() => {
        // WAS (until #332): if (hasFetchedFairs.current) return; hasFetchedFairs.current = true;
        if (hasFetchedFairs.current === selectedLang) return;
        hasFetchedFairs.current = selectedLang;

        const fetchFairsAndSetLogo = async () => {
            try {
                // Backlog #187: GET /fuar/list is every fair, not the company's.
                if (firmaAccount) {
                    setFairs(await getSelectableFairs());
                } else {
                    const data = await getFairs();
                    setFairs(data.data);
                }
            } catch (error) {
                console.error("Fuar listesi yüklenirken hata:", error);
                setFairsLoadFailed(true);
            }
        };
        fetchFairsAndSetLogo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLang]); // WAS (until #332): [] // sadece mount'ta çalış

    // Backlog #44: the switcher offers the ACTIVE fairs (the ones actually worth quick-switching
    // between) plus the currently selected fair even when it has since left ACTIVE (a past fair
    // someone is still reviewing shouldn't just disappear from its own picker). Everything else is a
    // click away on the full Fuar List, linked below the list instead of duplicated here.
    // Backlog #187: a firma account gets its company's whole list instead — past fairs included (a
    // company reviews its old fairs too), already newest first — and has no Fuar List to fall back on.
    const modalFairs = useMemo(() => {
        if (firmaAccount) return fairs;
        const active = fairs.filter(f => f.group === 'ACTIVE');
        const selected = fairs.find(f => f.id === selectedFuarId);
        const withSelected = selected && !active.some(f => f.id === selected.id)
            ? [selected, ...active]
            : active;
        return withSelected.slice(0, 8);
    }, [fairs, selectedFuarId, firmaAccount]);

    const selectedFair = useMemo(() => fairs.find(f => f.id === selectedFuarId), [fairs, selectedFuarId]);

    // selectedFuarId değişince logoyu güncelle
    useEffect(() => {
        async function run() {
            if (!selectedFuarId || fairs.length === 0) return;
            const activeFair = fairs.find(f => f.id === selectedFuarId);
            // Logosu olmayan fuar varsayılan görseli alır (backlog #18) — öncekinin logosu kalmaz.
            if (activeFair) {
                setActiveFairLogo({ url: activeFair.logoUrl, fileName: activeFair.logo });
            }
        }
        run();
    }, [selectedFuarId, fairs]);


    // Modal Aç/Kapat
    const toggleModal = () => {
        setModalOpen(!modalOpen);
        setActivateError(null);
    };

    const toogleMenuBtn = () => {
        const hamburgerIcon = document.querySelector(".hamburger-icon");
        if (hamburgerIcon) {
            hamburgerIcon.classList.toggle("open");
        } else {
            console.warn("Hamburger icon bulunamadı. DOM yapısını kontrol edin.");
        }

        const windowSize = document.documentElement.clientWidth;
        dispatch(changeSidebarVisibility("show"));

        if (document.documentElement.getAttribute("data-layout") === "horizontal") {
            document.body.classList.toggle("menu");
        }

        if (
            sidebarVisibilitytype === "show" &&
            (document.documentElement.getAttribute("data-layout") === "vertical" ||
                document.documentElement.getAttribute("data-layout") === "semibox")
        ) {
            if (windowSize < 1025 && windowSize > 767) {
                document.body.classList.remove("vertical-sidebar-enable");
                document.documentElement.setAttribute(
                    "data-sidebar-size",
                    document.documentElement.getAttribute("data-sidebar-size") === "sm" ? "" : "sm"
                );
            } else if (windowSize > 1025) {
                document.body.classList.remove("vertical-sidebar-enable");
                document.documentElement.setAttribute(
                    "data-sidebar-size",
                    document.documentElement.getAttribute("data-sidebar-size") === "lg" ? "sm" : "lg"
                );
            } else if (windowSize <= 767) {
                document.body.classList.add("vertical-sidebar-enable");
                document.documentElement.setAttribute("data-sidebar-size", "lg");
            }
        }

        if (document.documentElement.getAttribute("data-layout") === "twocolumn") {
            document.body.classList.toggle("twocolumn-panel");
        }
    };

    useEffect(() => {
        const hamburgerIcon = document.querySelector(".hamburger-icon");
        if(menuItemClicked && hamburgerIcon.classList && hamburgerIcon.classList.contains("open")){
            console.log(hamburgerIcon.classList);
            toogleMenuBtn();
        }
        async function resetClick() { setMenuItemClicked(false); }
        resetClick();
    }, [menuItemClicked, toogleMenuBtn]);

    const handleActivateFair = async (fairId) => {
        console.log('📌 Fuar aktif etme başladı:', fairId);
        setActivateError(null);
        try {

            await activateFair(userId, fairId);
            console.log('✅ activateFair() başarılı');
            setSelectedFuarId(fairId);
            // Seçilen fuarın logosunu hemen güncelle
            const selected = fairs.find(f => f.id === fairId);
            if (selected) setActiveFairLogo({ url: selected.logoUrl, fileName: selected.logo });
            // Backlog #44: WAS also `localStorage.setItem("user", JSON.stringify({ ...user, fuarId }))`
            // — a second, independent copy of the active fair nothing reads any more now that the old
            // /Fairs page (its only reader) redirects to /Management/Fairs. activeFairId below (read
            // by MainContext.fetchActiveFairId's own fallback) is the one copy that matters.
            localStorage.setItem("activeFairId", String(fairId));
            setModalOpen(false);
            // window.location.reload() KALDIRILDI - sonsuz döngüye giriyordu
        } catch (error) {
            console.error("Fuar aktif etme hatası:", error);
            // Backlog #187: e.g. the 403 for a fair outside the company's list (FairContext/fuarService
            // already turned it into a Turkish message). Shown inside the modal: there is no app-wide
            // ToastContainer, so a toast from the Header would be lost on most pages.
            setActivateError(apiErrorMessage(error, t("fuarSecilemedi")));
        }
    };

    const formatFairDate = (startDate, endDate) => {
        // WAS (until #332): new Intl.DateTimeFormat(selectedLang === "tr" ? "tr-TR" : "en-US", {
        const formatter = new Intl.DateTimeFormat(currentLocaleTag(), {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
        return `${formatter.format(new Date(startDate))} - ${formatter.format(new Date(endDate))}`;
    };
    

    return (
        <React.Fragment>
            <header id="page-topbar" className={headerClass}>
                <div className="layout-width">
                    <div className="navbar-header">
                        <div className="d-flex align-items-center">
                            {/* Sol tarafta aktif fuar logosu */}
                            <button
                                onClick={toogleMenuBtn}
                                type="button"
                                className="btn btn-sm px-3 fs-16 header-item vertical-menu-btn topnav-hamburger"
                                id="topnav-hamburger-icon">
                                <span className="hamburger-icon">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </span>
                            </button>
                            <div className="d-flex justify-content-end align-items-center">
                                {/* #621: a company login's fair logo opens Fair Information (the fair's stage dates) and nothing else; staff keep
                                    logo = Select Fair. WAS (every login): cursor "pointer" + onClick={toggleModal}. */}
                                {activeFairLogo ? (
                                    <FuarLogoImg
                                        url={activeFairLogo.url}
                                        fileName={activeFairLogo.fileName}
                                        alt={t("aktifFuarLogosu")}
                                        style={{
                                            borderRadius: "5px",
                                            maxHeight: "50px",
                                            maxWidth: "100px",
                                            marginRight: "10px",
                                            cursor: !firmaAccount || selectedFair?.detay ? "pointer" : undefined,
                                        }}
                                        {...(firmaAccount ? (selectedFair?.detay ? {
                                            role: "button",
                                            tabIndex: 0,
                                            title: t("fuarBilgisi"),
                                            "aria-label": t("fuarBilgisi"),
                                            onClick: () => setDetayFuar(selectedFair.detay),
                                            onKeyDown: (e) => { if (e.key === "Enter") setDetayFuar(selectedFair.detay); },
                                        } : {}) : { onClick: toggleModal })}
                                    />
                                ) : null}
                                <Button
                                    color="primary"
                                    size="sm"
                                    outline
                                    onClick={toggleModal}
                                    style={{ marginRight: "10px", whiteSpace: "nowrap" }}
                                >
                                    <i className="ri-calendar-event-line me-1"></i>
                                    {t("fuarSec")}
                                </Button>
                                {/* #561: the selected fair's read-only window (stage dates). #621: a company login opens it from the fair logo instead,
                                    so the button is staff-only now. WAS: {selectedFair?.detay ? ( for everyone. */}
                                {!firmaAccount && selectedFair?.detay ? (
                                    <Button
                                        onClick={() => setDetayFuar(selectedFair.detay)}
                                        color="primary"
                                        size="sm"
                                        outline
                                        title={t("fuarBilgisi")}
                                        aria-label={t("fuarBilgisi")}
                                        style={{ marginRight: "10px", whiteSpace: "nowrap" }}
                                    >
                                        <i className="ri-information-line" />
                                        <span className="d-none d-lg-inline ms-1">{t("fuarBilgisi")}</span>
                                    </Button>
                                ) : null}
                                {/* #311: rol değiştirici — firmanın bu fuardaki arayüz setleri (ör. kendi dekoratörü olan
                                    katılımcı: Katılımcı + Dekoratör). Seçim fuar başına hatırlanır (PortalContext). */}
                                {portal?.arayuzler?.length > 1 && (
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: "auto", marginRight: "10px" }}
                                        aria-label={t("arayuz")}
                                        title={t("arayuzIpucu")}
                                        value={portal.aktif?.kod ?? ""}
                                        onChange={(e) => portal.setArayuz(e.target.value)}
                                    >
                                        {portal.arayuzler.map((a) => <option key={a.kod} value={a.kod}>{t(`portal:arayuz.${a.kod}`, { defaultValue: a.ad })}</option>)}
                                    </select>
                                )}
                                {SHOW_PANEL_NAME_PILL && portal?.arayuzler?.length === 1 && portal.aktif && (
                                    <Badge color="primary" pill className="me-2 d-none d-md-inline-block">{t(`portal:arayuz.${portal.aktif.kod}`, { defaultValue: portal.aktif.ad })}</Badge>
                                )}
                                {/* #418: the login's portal role(s) in this company, in the session's language (the backend names them in
                                    the request's language; PortalContext refetches when the language changes). */}
                                {portal?.kullaniciRolleri?.length > 0 && (
                                    <span className="me-2 d-none d-md-inline-flex align-items-center gap-1 small text-muted" data-testid="portal-roller"
                                        title={t("portal:rolBasligi")}>
                                        <i className="ri-user-settings-line" />
                                        {portal.kullaniciRolleri.map((r) => (
                                            <Badge color="light" className="text-dark border fw-normal" key={r.kod} data-rol={r.kod}>{r.ad}</Badge>
                                        ))}
                                    </span>
                                )}
                            </div>

                            {/* #623: a company login's product logo is not a link ("/" is a staff page: it showed the "open to SAKA staff" lock).
                                WAS (every login): both logos were <Link to="/" className="logo logo-dark|logo-light">. */}
                            <div className="navbar-brand-box horizontal-logo">
                                {[
                                    { cls: "logo-dark", lg: logoDark, smHeight: "30" },
                                    { cls: "logo-light", lg: logoLight, smHeight: "22" },
                                ].map(({ cls, lg, smHeight }) => {
                                    const icerik = (
                                        <>
                                            <span className="logo-sm">
                                                <img src={logoSm} alt="" height={smHeight} />
                                            </span>
                                            <span className="logo-lg">
                                                <img src={lg} alt="" height="17" />
                                            </span>
                                        </>
                                    );
                                    return firmaAccount
                                        ? <span key={cls} className={`logo ${cls}`}>{icerik}</span>
                                        : <Link key={cls} to="/" className={`logo ${cls}`}>{icerik}</Link>;
                                })}
                            </div>
                        </div>

                        <div className="d-flex align-items-center">
                            <FirmaEgitimi />
                            <LanguageDropdown selectedLang={selectedLang} setSelectedLang={setSelectedLang} />

                            <LightDark
                                layoutMode={layoutModeType}
                                onChangeLayoutMode={onChangeLayoutMode}
                            />

                            <ProfileDropdown />
                        </div>
                    </div>
                </div>
            </header>

            {/* Modal */}
            <Modal isOpen={modalOpen} toggle={toggleModal} scrollable>
                <ModalHeader toggle={toggleModal}>
                    {t("fuarSecimi")}
                </ModalHeader>
                <ModalBody>
                    {activateError && <Alert color="danger">{activateError}</Alert>}
                    {modalFairs.length === 0 ? (
                        <div className="text-center text-muted py-4">
                            <i className="ri-calendar-event-line fs-2 mb-2 d-block"></i>
                            <p>
                                {fairsLoadFailed
                                    ? t("fuarListesiYuklenemedi")
                                    : firmaAccount
                                        ? t("firmaFuaraBagliDegil")
                                        : t("aktifFuarYok")}
                            </p>
                        </div>
                    ) : (
                        modalFairs.map((fair) => (
                        <Card
                            key={fair.id}
                            className="mb-3 p-3 ribbon-box position-relative"
                            style={{
                                borderRadius: "10px",
                                border: fair.id === selectedFuarId ? "2px solid #007bff" : "1px solid #ddd",
                                cursor: "pointer",
                            }}
                            onClick={() => handleActivateFair(fair.id)} // Fuar seçim fonksiyonu
                        >
                            <Row className="align-items-center">
                                {/* Logo */}
                                <Col md="2" className="text-center">
                                    <FuarLogoImg
                                        url={fair.logoUrl}
                                        fileName={fair.logo}
                                        alt={fair.nameTR || fair.nameEN}
                                        style={{
                                            maxHeight: "80px",
                                            maxWidth: "100%",
                                            borderRadius: "5px",
                                        }}
                                    />
                                </Col>

                                {/* Fuar Bilgileri */}
                                <Col md="7">
                                    <h5 style={{ fontWeight: "bold" }}>
                                        {/* WAS (until #332): selectedLang === "tr" ? fair.nameTR : fair.nameEN -- the backend now answers the name in the UI language */}
                                        {fair.nameTR || fair.nameEN}
                                    </h5>
                                    <p className="text-muted" style={{ marginBottom: "5px" }}>
                                        {formatFairDate(fair.start_date, fair.end_date)}
                                    </p>
                                    <p>{fair.areaName}</p>
                                </Col>

                                {/* Backlog #44: was "Aktif"/"Aktif Et" — collided with the Fuar
                                    List's own date-based "Aktif" group (#45/#46). "Seçili"/"Seç"
                                    matches the wording FuarListPage already uses for this action. */}
                                <Col md="3" className="text-end">
                                    {/* #561: stage dates of this fair, without selecting it */}
                                    {fair.detay && (
                                        <Button
                                            size="sm"
                                            color="primary"
                                            outline
                                            className="me-2"
                                            onClick={(e) => { e.stopPropagation(); setDetayFuar(fair.detay); }}
                                            title={t("fuarBilgisi")}
                                            aria-label={t("fuarBilgisi")}
                                        >
                                            <i className="ri-information-line" />
                                        </Button>
                                    )}
                                    {fair.id === selectedFuarId ? (
                                        <Badge color="success">{t("secili")}</Badge>
                                    ) : (
                                        <Button color="primary">
                                            {t("sec")}
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        </Card>
                    ))
                    )}
                    {/* Backlog #187: /Management/Fairs is staff-only ("Yetkiniz yok" for 9/19). */}
                    {!firmaAccount && (
                        <div className="text-center mt-2">
                            <Link to="/Management/Fairs" onClick={toggleModal}>
                                {t("tumFuarlar")}
                            </Link>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* #561: opens over the Fuar Seç window, which stays where it was */}
            <FuarBilgisiModal fuar={detayFuar} isOpen={Boolean(detayFuar)} toggle={() => setDetayFuar(null)} />

        </React.Fragment>
    );
};

export default Header;
